import { useEffect, useRef } from "react";

const WORLDS = [
  { num: "01", label: "VENTURES", desc: "I back bold founders with capital and conviction.", cta: "PITCH ME →", url: "https://venture.shashankupadhyaya.com" },
  { num: "02", label: "STUDIO",   desc: "Shashank Upadhyaya Production — original music.",  cta: "LISTEN →",   url: "https://studio.shashankupadhyaya.com" },
  { num: "03", label: "FINANCE",  desc: "Teaching markets, investing, and building wealth.", cta: "READ →",     url: "https://finance.shashankupadhyaya.com" },
  { num: "04", label: "BUILD",    desc: "AI tools and products, built in public.",           cta: "EXPLORE →",  url: "https://build.shashankupadhyaya.com" },
  { num: "05", label: "DEVELOP",  desc: "Custom websites for local service businesses.",      cta: "GET STARTED →", url: "https://develop.shashankupadhyaya.com" },
];

const SOCIALS = [
  { label: "LinkedIn",  url: "https://www.linkedin.com/in/shaxhxnk/" },
  { label: "Instagram", url: "https://www.instagram.com/shaxhxnk/" },
  { label: "GitHub",    url: "https://github.com/shaxhxnk" },
  { label: "YouTube",   url: "https://www.youtube.com/@shaxhxnk" },
];

const TICKER = "INVESTOR / FOUNDER / DEVELOPER / MUSIC PRODUCER / FINANCE / WRITER / CREATOR / ";

function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add("br-in"); obs.unobserve(el); } },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Fade({ as: Tag = "div", delay = 0, className = "", style = {}, children, ...rest }) {
  const ref = useFadeIn();
  return (
    <Tag
      ref={ref}
      className={`br-fade ${className}`}
      style={{ transitionDelay: `${delay * 0.12}s`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default function Portfolio() {
  const tick = TICKER.repeat(12);
  return (
    <div className="br">
      <style>{CSS}</style>

      {/* 1. Top Bar */}
      <div className="br-topbar-line">
        <Fade className="br-topbar">
          <img src={require("../assets/logo_nobg.png")} alt="S/U" className="br-topbar__logo" />
          <span className="br-topbar__right">PORTFOLIO ©2026</span>
        </Fade>
      </div>

      {/* 2. Marquee */}
      <div className="br-marquee-wrap" aria-hidden="true">
        <div className="br-marquee-track">
          <span className="br-marquee-copy">{tick}</span>
          <span className="br-marquee-copy">{tick}</span>
        </div>
      </div>

      {/* 3. Hero */}
      <div className="br-container">
        <section className="br-hero">
          <div className="br-hero__left">
            <Fade delay={1} className="br-eyebrow">● BUILDING AN EMPIRE</Fade>
            <Fade delay={2}>
              <h1 className="br-h1">
                <span className="br-h1__solid">Shashank</span>
                <span className="br-h1__outline">Upadhyaya</span>
              </h1>
            </Fade>
          </div>
          <Fade delay={3} className="br-hero__right">
            <p className="br-bio">
              I build companies, back founders, ship AI products, produce original music, and teach markets.
            </p>
            <div className="br-socials">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.url} target="_blank" rel="noreferrer" className="br-social-link">
                  <span>{s.label}</span>
                  <span>↗</span>
                </a>
              ))}
            </div>
          </Fade>
        </section>
      </div>

      {/* 4. Worlds */}
      <div className="br-container">
        <Fade className="br-worlds-label">
          <span>[ My Worlds ]</span>
          <span>04 / 04</span>
        </Fade>
      </div>

      <div className="br-worlds">
        {WORLDS.map((w, i) => (
          <Fade key={w.num} delay={i * 0.5 + 1}>
            <a href={w.url} target="_blank" rel="noreferrer" className="br-world-row">
              <div className="br-world-row__inner">
                <span className="br-world-num">{w.num}</span>
                <div className="br-world-mid">
                  <span className="br-world-title">{w.label}</span>
                  <span className="br-world-desc">{w.desc}</span>
                </div>
                <span className="br-world-cta">{w.cta}</span>
              </div>
            </a>
          </Fade>
        ))}
      </div>

      {/* 5. Contact */}
      <Fade delay={1} className="br-contact">
        <h2 className="br-contact__h2">Got something /<br />worth building?</h2>
        <a href="mailto:mail@shashankupadhyaya.com" className="br-contact__btn">
          mail@shashankupadhyaya.com
        </a>
      </Fade>

      {/* 6. Footer */}
      <div className="br-container">
        <Fade className="br-footer">
          <span>© 2026 Shashank Upadhyaya</span>
          <span>shashankupadhyaya.com</span>
        </Fade>
      </div>
    </div>
  );
}

const CSS = `
  /* ── Variables & Base ─────────────────────────────────── */
  .br {
    --bg: #101010;
    --text: #f2f2ef;
    --red: #C7F94B;
    --hair: #2a2320;
    --muted: #8a7d79;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
  .br *, .br *::before, .br *::after { box-sizing: border-box; margin: 0; padding: 0; font-family: inherit; }
  .br a { color: inherit; text-decoration: none; }

  /* ── Fade animation ───────────────────────────────────── */
  .br-fade {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1),
                transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .br-fade.br-in { opacity: 1; transform: none; }

  /* ── Container ────────────────────────────────────────── */
  .br-container { max-width: 1300px; margin: 0 auto; padding: 0 40px; }

  /* ── 1. Top Bar ───────────────────────────────────────── */
  .br-topbar-line { border-bottom: 1.5px solid var(--hair); }
  .br-topbar {
    max-width: 1300px;
    margin: 0 auto;
    padding: 20px 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .br-topbar__logo {
    height: 60px;
    width: auto;
    display: block;
    object-fit: contain;
  }
  .br-topbar__right {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
  }

  /* ── 2. Marquee ───────────────────────────────────────── */
  .br-marquee-wrap {
    background: var(--red);
    overflow: hidden;
    padding: 13px 0;
  }
  .br-marquee-track {
    display: flex;
    width: max-content;
    animation: br-scroll 90s linear infinite;
    will-change: transform;
  }
  .br-marquee-copy {
    font-family: 'Space Mono', monospace;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #000;
    white-space: nowrap;
    flex-shrink: 0;
  }
  @keyframes br-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }

  /* ── 3. Hero ──────────────────────────────────────────── */
  .br-hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 0 56px;
    min-height: calc(100vh - 106px);
    padding: 48px 0;
    border-bottom: 1.5px solid var(--hair);
    align-items: center;
  }
  .br-hero__left { display: flex; flex-direction: column; gap: 28px; min-width: 0; }
  .br-eyebrow {
    font-family: 'Space Mono', monospace;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--red);
  }
  .br-h1 {
    display: flex;
    flex-direction: column;
    font-family: 'Archivo', sans-serif;
    font-weight: 900;
    font-size: clamp(32px, 10vw, 148px);
    line-height: 0.84;
    letter-spacing: -0.045em;
    text-transform: uppercase;
  }
  .br-h1__solid  { color: var(--text); }
  .br-h1__outline {
    color: transparent;
    -webkit-text-stroke: 2px var(--red);
    text-stroke: 2px var(--red);
  }

  /* Hero right panel */
  .br-hero__right {
    border-left: 2px solid var(--hair);
    padding-left: 32px;
    padding-top: 52px;
    display: flex;
    flex-direction: column;
  }
  .br-bio {
    font-family: 'Space Mono', monospace;
    font-size: 12px;
    line-height: 1.85;
    color: var(--muted);
    margin-bottom: 32px;
  }
  .br-socials { display: flex; flex-direction: column; }
  .br-social-link {
    font-family: 'Space Mono', monospace;
    font-weight: 700;
    font-size: 10.5px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 0;
    border-top: 1.5px solid var(--hair);
    transition: color 0.18s ease, padding-left 0.18s ease;
  }
  .br-social-link:hover { color: var(--red); padding-left: 8px; }

  /* ── 4. Worlds ────────────────────────────────────────── */
  .br-worlds-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 44px 0 20px;
    font-family: 'Space Mono', monospace;
    font-size: 10.5px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .br-world-row {
    display: block;
    border-top: 1.5px solid var(--hair);
    color: var(--text);
    text-decoration: none;
    transition: background 0.18s ease, color 0.18s ease;
  }
  .br-worlds .br-fade:last-child .br-world-row { border-bottom: 1.5px solid var(--hair); }
  .br-world-row:hover               { background: var(--red); color: #000; }
  .br-world-row:hover .br-world-num  { color: #000; }
  .br-world-row:hover .br-world-desc { color: #000; }
  .br-world-row__inner {
    max-width: 1300px;
    margin: 0 auto;
    padding: 28px 40px;
    display: grid;
    grid-template-columns: 120px 1fr auto;
    align-items: center;
    gap: 32px;
  }
  .br-world-num {
    font-family: 'Archivo', sans-serif;
    font-weight: 900;
    font-size: clamp(52px, 6.5vw, 104px);
    letter-spacing: -0.045em;
    color: var(--muted);
    line-height: 1;
    transition: color 0.18s ease;
  }
  .br-world-mid { display: flex; flex-direction: column; gap: 6px; }
  .br-world-title {
    font-family: 'Archivo', sans-serif;
    font-weight: 800;
    font-size: clamp(20px, 2.5vw, 36px);
    letter-spacing: -0.025em;
    text-transform: uppercase;
  }
  .br-world-desc {
    font-family: 'Space Mono', monospace;
    font-size: 11.5px;
    color: var(--muted);
    letter-spacing: 0.04em;
    line-height: 1.6;
    transition: color 0.18s ease;
  }
  .br-world-cta {
    font-family: 'Space Mono', monospace;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  /* ── 5. Contact ───────────────────────────────────────── */
  .br-contact {
    background: var(--red);
    color: #000;
    text-align: center;
    padding: 120px 40px;
    margin-top: 72px;
  }
  .br-contact__h2 {
    font-family: 'Archivo', sans-serif;
    font-weight: 900;
    font-size: clamp(40px, 7.5vw, 100px);
    letter-spacing: -0.045em;
    text-transform: uppercase;
    line-height: 0.90;
    margin-bottom: 52px;
  }
  .br-contact .br-contact__btn {
    display: inline-block;
    background: #000;
    color: #f2f2ef;
    font-family: 'Space Mono', monospace;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 18px 36px;
    max-width: 100%;
    word-break: break-all;
    box-sizing: border-box;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
  }
  .br-contact .br-contact__btn:hover {
    transform: translate(-4px, -4px);
    box-shadow: 4px 4px 0 rgba(255,255,255,0.85);
    color: #f2f2ef;
  }

  /* ── 6. Footer ────────────────────────────────────────── */
  .br-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 28px 0;
    flex-wrap: wrap;
    gap: 12px;
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
    border-top: 1.5px solid var(--hair);
  }

  /* ── Responsive ───────────────────────────────────────── */
  @media (max-width: 960px) {
    .br-hero { grid-template-columns: 1fr; gap: 48px; }
    .br-hero__right {
      border-left: none;
      border-top: 1.5px solid var(--hair);
      padding-left: 0;
      padding-top: 40px;
    }
  }
  @media (max-width: 768px) {
    .br-container { padding: 0 24px; }
    .br-topbar { padding: 16px 24px; }
    .br-hero { min-height: calc(100svh - 96px); padding: 40px 0; }
    .br-world-row__inner { padding: 20px 24px; grid-template-columns: 80px 1fr; gap: 16px; }
    .br-world-cta { display: none; }
    .br-worlds-label { padding: 36px 0 16px; }
    .br-contact { padding: 80px 24px; margin-top: 56px; }
    .br-footer { padding: 20px 0; }
  }
  @media (max-width: 480px) {
    .br-topbar__logo { height: 36px; }
    .br-topbar__right { display: none; }
    .br-topbar { padding: 12px 16px; }
    .br-world-row__inner { grid-template-columns: 48px 1fr; gap: 12px; padding: 16px; }
    .br-world-num { font-size: clamp(24px, 7vw, 48px); }
    .br-contact { padding: 64px 16px; }
    .br-contact__h2 { margin-bottom: 28px; }
    .br-contact__btn { padding: 14px 20px; }
  }
`;
